import { CardHeader, CardHeaderProps } from "@material-tailwind/react";
import { CloudOff } from "@mui/icons-material";
import { CircularProgress, IconButton } from "@mui/material";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { SyncIcon } from "~/src/components/Icons/Icons";
import Text, { Header, TextVariant } from "~/src/components/Text/Text";
import AppDataContext from "~/src/contexts/AppDataContext/DataProvider";
import { StorageContext } from "~/src/contexts/StorageContext/StorageProvider";
import { useClientState } from "~/src/hooks/useClientState";
import { RouteProps } from "../../../../hooks/useRouteProps";

/**
 * @typedef {Omit<CardHeaderProps, "ref" | "children">} CardHeaderDefaultProps
 * @property {string | number} id - A unique identifier for the card header.
 * @property {RouteProps} [pathProps] - Object that contains the left action, right action, `onLeftActionClick` function, and `onRightActionClick` function.
 */
export type CardHeaderDefaultProps = Omit<CardHeaderProps, "ref" | "children"> & {
  pathProps: RouteProps;
  className?: string;
  dynamicTitle?: string;
};

/**
 * CardHeaderDefault is a React component that renders a card header with optional left and right actions.
 * @param {CardHeaderDefaultProps} props - The properties that define the CardHeaderDefault component.
 * @param {React.Ref<HTMLDivElement>} ref - The ref to be forwarded to the CardHeader component.
 * @returns {React.ReactElement} The CardHeaderDefault component.
 */

const CardHeaderDefault: React.ForwardRefRenderFunction<HTMLDivElement, CardHeaderDefaultProps> = (
  { pathProps, className, dynamicTitle, ...rest },
  ref,
) => {
  const { pageTitle, onLeftActionClick, dialogs } = pathProps;
  const appData = useContext(AppDataContext);
  const clientState = useClientState();
  const storage = useContext(StorageContext);
  const { t } = useTranslation();
  const title =
    (pathProps.useDynamicHeader && dynamicTitle) ||
    (pageTitle !== null && pageTitle !== undefined && pageTitle !== "" && pageTitle) ||
    "";

  const showOfflineIndicator = clientState === "Offline" || clientState === "Degraded";
  const showSyncIndicator = clientState === "Online" && storage.anyPending();

  return (
    <CardHeader
      id={"header"}
      data-testid={`card-header-${title}-id`}
      ref={ref}
      floated={false}
      shadow={false} //using custom shadow instead
      className={`w-full  bg-ldir-blue-900 mx-0 mt-0 rounded-none overflow-visible ${className} grid grid-cols-[65px_1fr_65px] items-center z-10`}
      {...rest}
    >
      {onLeftActionClick != undefined ? (
        <div>
          <IconButton
            onClick={onLeftActionClick}
            className="pr-6 py-4 pl-4 rounded-none focus-visible:outline-4 focus-visible:outline-ldir-blue-600"
            title="Tilbake"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="17"
              height="31"
              viewBox="0 0 17 31"
              fill="none"
              role="graphics-symbol"
            >
              <path
                d="M13.543 29.333L1.65234 15.9951L1.35547 15.6621L1.65234 15.3291L13.543 1.99219L13.916 1.57324L14.2891 1.99219L15.373 3.20801L15.6699 3.54004L15.373 3.87305L4.86328 15.6621L15.373 27.4512L15.6699 27.7842L15.373 28.1172L14.2891 29.333L13.916 29.751L13.543 29.333Z"
                fill="#FCFAF6"
                stroke="#FCFAF6"
              />
            </svg>
          </IconButton>
        </div>
      ) : (
        <div />
      )}
      <div className=" font-normal text-center py-4">
        {title ? (
          <Header
            headerLvl={1}
            variant={TextVariant.xlarge}
            className="text-ldir-brown-300 font-normal"
          >
            {title}
          </Header>
        ) : (
          <div className="h-8" />
        )}
        {(showOfflineIndicator || showSyncIndicator) && pathProps.showLastSyncedText && (
          <Text variant={TextVariant.small} className="text-ldir-brown-300/75">
            {`${t("sync.sistSynkronisert")} ${appData.sync.lastSynced.value ?? t("sync.ukjent")}`}
          </Text>
        )}
      </div>
      <div>
        {showOfflineIndicator ? (
          <CloudOff className={"text-ldir-blue-paler"} />
        ) : appData.sync.isSyncing.value ? (
          <CircularProgress className="text-white" />
        ) : showSyncIndicator ? (
          <button
            title={t("actions.sync")}
            type="button"
            onClick={() => window.location.reload()}
            className="text-ldir-blue-100 flex bg-ldir-blue-100/10 py-2 px-4 rounded-full border-ldir-blue-100 border-[3px]!"
          >
            <SyncIcon className={"text-ldir-blue-paler"} title={t("actions.sync")} />
          </button>
        ) : (
          <div />
        )}
      </div>
      {dialogs &&
        dialogs.map((dialog, index) => <React.Fragment key={index}>{dialog}</React.Fragment>)}
    </CardHeader>
  );
};

export default React.forwardRef(CardHeaderDefault);
